import type { HintLevel } from '@codeframe/lang-core';
import { useMe, useUpdateSettings } from '../api/hooks.js';
import { Loading, Page, PageHeader, errorMessage } from '../components/Page.js';
import { Alert, Badge, Field, Meter, Panel, Stat, selectClass } from '../components/ui.js';

const HINT_LEVELS: { id: HintLevel; label: string; description: string }[] = [
  { id: 'beginner', label: 'Beginner', description: 'Full explanations with examples.' },
  { id: 'normal', label: 'Normal', description: 'Short, direct guidance.' },
  { id: 'advanced', label: 'Advanced', description: 'Minimal nudges.' },
  { id: 'expert', label: 'Expert', description: 'Just the facts.' },
];

export function ProfilePage() {
  const { data: me } = useMe();
  const update = useUpdateSettings();
  if (!me) return <Loading />;
  const p = me.player;
  return (
    <Page>
      <PageHeader eyebrow="Pilot" title={me.user.username} description={`Enlisted ${new Date(p.createdAt).toLocaleDateString()}`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <Panel label="Levels">
          <div style={{ display: 'grid', gap: 14 }}>
            <Meter label={`Player level ${p.level.level}`} value={p.level.xp - p.level.levelStartXp} max={(p.level.nextLevelXp ?? p.level.xp + 1) - p.level.levelStartXp} display={`${p.level.xp} XP`} tall />
            <Meter label={`Programming level ${p.programming.level}`} value={p.programming.xp - p.programming.levelStartXp} max={(p.programming.nextLevelXp ?? p.programming.xp + 1) - p.programming.levelStartXp} display={`${p.programming.xp} XP`} tall />
          </div>
        </Panel>
        <Panel label="Language mastery">
          <div style={{ display: 'grid', gap: 14 }}>
            {p.languages.map((l) => (
              <Meter key={l.language} label={l.displayName} value={l.percent} max={100} display={`${l.percent}%`} color="var(--cyber)" tall />
            ))}
            <span className="muted">JavaScript and Lua/Luau join the Academy in a future update.</span>
          </div>
        </Panel>
        <Panel label="Disciplines">
          <div style={{ display: 'grid', gap: 10 }}>
            {p.disciplines.map((d) => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }} title={d.description}>
                <span>{d.name}</span>
                <span className="mono">{d.xp} XP</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel label="Concepts mastered">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {p.concepts.length === 0 && <span className="muted">Complete lessons to master concepts.</span>}
            {p.concepts.map((c) => (
              <Badge key={c.id} tone="accent">
                {c.name} · {c.xp}
              </Badge>
            ))}
          </div>
        </Panel>
        <Panel label="Settings">
          <div style={{ display: 'grid', gap: 14 }}>
            <Field label="Hint level">
              <select className={selectClass} value={p.settings.hintLevel} onChange={(e) => update.mutate({ hintLevel: e.target.value as HintLevel })}>
                {HINT_LEVELS.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.label} — {h.description}
                  </option>
                ))}
              </select>
            </Field>
            {update.error && <Alert>{errorMessage(update.error)}</Alert>}
          </div>
        </Panel>
        <Panel label="Service record">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            <Stat label="Lessons" value={p.stats.lessonsCompleted} />
            <Stat label="Modules" value={p.stats.modulesCompiled} />
            <Stat label="Battles won" value={p.stats.battlesWon} />
            <Stat label="Battles fought" value={p.stats.battlesFought} />
          </div>
        </Panel>
      </div>
    </Page>
  );
}
